import axios from 'axios'
import type { AxiosInstance, AxiosRequestConfig, AxiosResponse, InternalAxiosRequestConfig } from 'axios'
import { MeiucaServiceConfig, TokenStorage, RefreshTokenResponse } from './types'
import { CookieTokenStorage } from './CookieTokenStorage'
import {
  ApiError,
  BadRequestError,
  ForbiddenError,
  InternalError,
  MeiucaHttpError,
  MeiucaHttpErrorBody,
  NetworkError,
  NotFoundError,
  RateLimitedError,
  UnauthorizedError,
  UnknownError,
  ValidationError
} from './MeiucaHttpError'

// Refresh coordination lives at module level, not on the instance: an app
// creates one MeiucaService per domain service (the Arena dashboard has 28),
// and they all share a single refresh token through the cookie. The server
// rotates that token on first use, so two instances refreshing at once would
// leave the loser holding a revoked value and bounce the user to login. One
// in-flight refresh per cookie prefix is what every instance awaits.
const inflightRefreshes = new Map<string, Promise<string>>()

// Same race across browser tabs: the Web Locks API serialises the refresh
// between them where available; without it the per-tab coalescing still holds.
async function withRefreshLock<T>(name: string, run: () => Promise<T>): Promise<T> {
  const locks = typeof navigator !== 'undefined' ? navigator.locks : undefined
  if (locks && typeof locks.request === 'function') {
    return locks.request(name, run)
  }
  return run()
}

function isSessionRejection(error: any): boolean {
  const status = error?.response?.status
  return status === 401 || status === 403
}

export class MeiucaService {
  protected api: AxiosInstance
  private config: MeiucaServiceConfig
  protected tokenStorage: TokenStorage
  private isLoggingOut = false // Flag to prevent request loops during logout

  constructor(config: MeiucaServiceConfig, tokenStorage?: TokenStorage) {
    this.config = {
      tokenRefreshBuffer: 5 * 60 * 1000, // 5 minutes
      timeout: 30000, // 30 seconds
      cookiePrefix: '',
      refreshTokenCookieDays: 30,
      debug: false,
      ...config
    }

    this.tokenStorage = tokenStorage
      || new CookieTokenStorage(this.config.cookiePrefix, this.config.refreshTokenCookieDays)

    this.api = axios.create({
      baseURL: this.config.baseURL,
      timeout: this.config.timeout,
      headers: {
        'Content-Type': 'application/json',
      },
    })

    this.setupInterceptors()
  }

  private log(message: string, ...args: any[]): void {
    if (this.config.debug) {
      console.log(`[MeiucaService] ${message}`, ...args)
    }
  }

  private setupInterceptors() {
    // Request interceptor - adds token and handles proactive refresh
    this.api.interceptors.request.use(
      async (config: InternalAxiosRequestConfig) => {
        await this.ensureValidToken()

        const token = this.tokenStorage.getAccessToken()
        if (token) {
          config.headers.Authorization = `Bearer ${token}`
        }

        return config
      },
      (error) => Promise.reject(error)
    )

    // Response interceptor - handles token refresh with request queue
    this.api.interceptors.response.use(
      (response: AxiosResponse) => response,
      async (error) => {
        // If we're already logging out, reject immediately
        if (this.isLoggingOut) {
          return Promise.reject(error)
        }

        if (error.response?.status === 401 && !error.config._retry) {
          error.config._retry = true

          // Every caller awaits the same in-flight refresh (see
          // `inflightRefreshes`), so there is no queue to keep here: when it
          // settles, each failed request retries itself with the new token.
          try {
            const newToken = await this.refreshAccessToken()
            if (newToken) {
              error.config.headers.Authorization = `Bearer ${newToken}`
              return this.api.request(error.config)
            }
          } catch (refreshError) {
            // `performTokenRefresh` already ended the session when the server
            // rejected the token. Anything else (network, 5xx) keeps the
            // session and just fails this request.
            return Promise.reject(refreshError)
          }
        }

        // A 401 on the already-retried request means the freshly refreshed
        // token isn't accepted either: the session is dead. Log out instead of
        // letting the caller render it as a generic error and leaving the user
        // stuck on a page that can't load anything.
        if (error.response?.status === 401 && error.config?._retry) {
          this.handleAuthenticationError()
        }

        return Promise.reject(error)
      }
    )
  }

  private async ensureValidToken(): Promise<void> {
    const token = this.tokenStorage.getAccessToken()

    // The access token cookie expires together with the token itself, so an
    // expired session shows up here as a *missing* access token. Refresh it
    // before the request leaves — otherwise it goes out with no Authorization
    // header and the API answers with an error the caller can't tell apart
    // from a real failure. A visitor with no refresh token is simply anonymous
    // (public pages also hit the API), so leave the request unauthenticated.
    if (!token) {
      if (this.tokenStorage.getRefreshToken()) {
        await this.refreshAccessToken()
      }
      return
    }

    const expirationTime = this.getTokenExpirationTime(token)
    if (!expirationTime) return

    const now = Date.now()
    const timeUntilExpiration = expirationTime - now

    // If token expires within buffer time, refresh it proactively
    if (timeUntilExpiration <= this.config.tokenRefreshBuffer!) {
      await this.refreshAccessToken()
    }
  }

  private getTokenExpirationTime(token: string): number | null {
    try {
      const payload = JSON.parse(atob(token.split('.')[1]))
      // The API names the claim `expiration`; `exp` is the RFC 7519 name it
      // also emits. Read both so either backend version works.
      const exp = payload.expiration ?? payload.exp
      return exp ? exp * 1000 : null // Convert to milliseconds
    } catch (error) {
      console.error('Error parsing JWT token:', error)
      return null
    }
  }

  private isExpiringSoon(accessToken: string): boolean {
    const expirationTime = this.getTokenExpirationTime(accessToken)
    if (!expirationTime) return false
    return expirationTime - Date.now() <= this.config.tokenRefreshBuffer!
  }

  private async refreshAccessToken(): Promise<string | null> {
    const key = this.config.cookiePrefix ?? ''

    // One refresh per app at a time, whichever instance started it.
    const inflight = inflightRefreshes.get(key)
    if (inflight) {
      return inflight
    }

    const refreshToken = this.tokenStorage.getRefreshToken()
    if (!refreshToken) {
      this.handleAuthenticationError()
      return null
    }

    const refresh = this.performTokenRefresh(refreshToken).finally(() => {
      if (inflightRefreshes.get(key) === refresh) {
        inflightRefreshes.delete(key)
      }
    })
    inflightRefreshes.set(key, refresh)
    return refresh
  }

  private async performTokenRefresh(refreshTokenAtStart: string): Promise<string> {
    const lockName = `meiuca-refresh:${this.config.cookiePrefix ?? ''}`

    return withRefreshLock(lockName, async () => {
      // Another tab may have rotated the token while this one waited for the
      // lock. If the cookies already carry a fresh pair, use it: sending the
      // value we started with would hit an already revoked token.
      const refreshToken = this.tokenStorage.getRefreshToken()
      if (!refreshToken) {
        this.handleAuthenticationError()
        throw new Error('Refresh token missing')
      }
      if (refreshToken !== refreshTokenAtStart) {
        const accessToken = this.tokenStorage.getAccessToken()
        if (accessToken && !this.isExpiringSoon(accessToken)) {
          this.log('Token already refreshed elsewhere, reusing it')
          return accessToken
        }
      }

      try {
        this.log('Attempting token refresh...', this.config.refreshEndpoint)

        const response = await axios.post(this.config.refreshEndpoint, {
          refreshToken,
        })

        const tokenData: RefreshTokenResponse = response.data.data
        const { accessToken, refreshToken: newRefreshToken } = tokenData

        // Store new tokens
        this.tokenStorage.setTokens(accessToken, newRefreshToken)

        this.log('Token refreshed successfully')
        return accessToken
      } catch (error: any) {
        this.log('Token refresh failed:', {
          url: this.config.refreshEndpoint,
          status: error.response?.status,
          statusText: error.response?.statusText,
          data: error.response?.data,
          message: error.message
        })
        // Only the server gets to end a session (401/403). A timeout, no
        // connectivity or a 5xx means "could not refresh right now": the
        // cookies stay so the next request can try again.
        if (isSessionRejection(error)) {
          this.handleAuthenticationError()
        }
        throw error
      }
    })
  }

  private handleAuthenticationError(): void {
    // Prevent multiple calls and request loops
    if (this.isLoggingOut) {
      return
    }
    
    this.isLoggingOut = true
    this.log('Authentication failed, handling error')
    this.tokenStorage.clearTokens()
    
    if (this.config.onAuthenticationError) {
      this.config.onAuthenticationError()
    } else {
      // Default behavior: redirect to root
      if (typeof window !== 'undefined') {
        window.location.replace('/')
      }
    }
  }

  public isAuthenticated(): boolean {
    return !!this.tokenStorage.getAccessToken() && !this.isLoggingOut
  }

  public isLoggedOut(): boolean {
    return this.isLoggingOut
  }

  // Helper methods for common HTTP operations
  public async get<T = any>(url: string, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> {
    try {
      return await this.api.get(url, config)
    } catch (error) {
      throw this.normalizeHttpError(error)
    }
  }

  public async post<T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> {
    try {
      return await this.api.post(url, data, config)
    } catch (error) {
      throw this.normalizeHttpError(error)
    }
  }

  public async put<T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> {
    try {
      return await this.api.put(url, data, config)
    } catch (error) {
      throw this.normalizeHttpError(error)
    }
  }

  public async patch<T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> {
    try {
      return await this.api.patch(url, data, config)
    } catch (error) {
      throw this.normalizeHttpError(error)
    }
  }

  public async delete<T = any>(url: string, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> {
    try {
      return await this.api.delete(url, config)
    } catch (error) {
      throw this.normalizeHttpError(error)
    }
  }

  private normalizeHttpError(error: any): MeiucaHttpError {
    if (error instanceof MeiucaHttpError) {
      return error
    }

    if (error?.response) {
      const status = error.response.status || 0
      const errorBody = error.response.data?.error

      if (errorBody?.code && errorBody?.message) {
        const body: MeiucaHttpErrorBody = {
          ...errorBody,
          status: errorBody.status ?? status
        }
        return this.buildHttpError(body, error.response, error.request)
      }

      const code = this.codeForStatus(status)
      return this.buildHttpError({ code, message: code, status }, error.response, error.request)
    }

    if (error?.code === 'NETWORK_ERROR') {
      return new NetworkError({ code: 'network_error', message: 'network_error', status: 0 })
    }

    return new UnknownError({ code: 'unknown_error', message: 'unknown_error', status: 0 })
  }

  private buildHttpError(body: MeiucaHttpErrorBody, response?: any, request?: any): MeiucaHttpError {
    switch (body.status) {
      case 400:
        return new BadRequestError(body, response, request)
      case 401:
        return new UnauthorizedError(body, response, request)
      case 403:
        return new ForbiddenError(body, response, request)
      case 404:
        return new NotFoundError(body, response, request)
      case 422:
        return new ValidationError(body, response, request)
      case 429:
        return new RateLimitedError(body, response, request)
      case 500:
        return new InternalError(body, response, request)
      default:
        return new ApiError(body, response, request)
    }
  }

  private codeForStatus(status: number): string {
    switch (status) {
      case 400:
        return 'bad_request'
      case 401:
        return 'unauthorized'
      case 403:
        return 'forbidden'
      case 404:
        return 'not_found'
      case 422:
        return 'validation_failed'
      case 429:
        return 'rate_limited'
      case 500:
        return 'internal_error'
      default:
        return 'api_error'
    }
  }

}
